/**
 * actionsColumn — A fixed utility column that provides "Open" and
 * inline detail toggle buttons for every row.
 *
 * Always inserted at position 1 (index 0) — first data column, right after checkbox.
 * Cannot be hidden, reordered, or resized by the user.
 */

import type { ColumnDef } from "@tanstack/react-table";
import { TableProperties, ArrowRight } from "lucide-react";

export type ActionColumnCallbacks = {
  onToggleDetail?: (rowName: string) => void;
  onOpen: (rowName: string) => void;
};

/** Column ID — used to identify and prevent duplicates */
export const ACTIONS_COLUMN_ID = "__actions";

/** Fixed insert position (0-indexed) — always 1st data column (right after checkbox) */
const INSERT_INDEX = 0;

/**
 * Creates the actions column definition.
 */
function createActionsColumn<T>(callbacks: ActionColumnCallbacks): ColumnDef<T, any> {
  return {
    id: ACTIONS_COLUMN_ID,
    header: "",
    size: 110,
    minSize: 60,
    enableSorting: false,
    enableColumnFilter: false,
    enableResizing: true,
    enableHiding: false,
    cell: (cellProps: any) => {
      const row = cellProps.row.original as Record<string, unknown>;
      const rowName = (row.name ?? row.id ?? row._id) as string | undefined;
      if (!rowName) return null;

      return (
        <div className="flex items-center justify-center gap-1">
          {callbacks.onToggleDetail && (
            <button
              type="button"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                callbacks.onToggleDetail!(rowName);
              }}
              className="flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-medium transition-colors border text-muted-foreground/70 hover:text-foreground bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 border-neutral-200 dark:border-neutral-700"
              aria-label="Toggle inline details"
            >
              <TableProperties className="h-3 w-3" />
            </button>
          )}
          <button
            type="button"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              callbacks.onOpen(rowName);
            }}
            className="flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-medium text-muted-foreground/70 hover:text-foreground bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 border border-neutral-200 dark:border-neutral-700 transition-colors"
            aria-label="Open detail page"
          >
            Open
            <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      );
    },
  };
}

/**
 * Inserts the actions column at position 1 (index 0) into any column array.
 * This makes it the first data column, right after the checkbox in the final table.
 * Prevents duplicate insertion by checking for existing ACTIONS_COLUMN_ID.
 */
export function injectActionsColumn<T>(
  cols: ColumnDef<T, any>[],
  callbacks: ActionColumnCallbacks,
): ColumnDef<T, any>[] {
  // Prevent duplicate
  if (cols.some((c) => c.id === ACTIONS_COLUMN_ID)) return cols;

  const actionsCol = createActionsColumn<T>(callbacks);
  const idx = Math.min(INSERT_INDEX, cols.length);
  const result = [...cols];
  result.splice(idx, 0, actionsCol);
  return result;
}
